import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { supabase } from "lib/supabase";

// Contexte d'authentification + organisation courante (rôle, profil).
const SessionContext = createContext(null);

export function SessionProvider({ children }) {
  const [authReady, setAuthReady] = useState(false);
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [org, setOrg] = useState(null);
  const [role, setRole] = useState(null);

  // 1) Suivi de la session — on NE FAIT PAS d'appel Supabase asynchrone ici
  //    (les awaits dans onAuthStateChange peuvent bloquer le verrou d'auth).
  useEffect(() => {
    let actif = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!actif) return;
      setSession(data.session);
      setAuthReady(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, sess) => {
      setSession(sess);
      setAuthReady(true);
    });
    return () => { actif = false; sub.subscription.unsubscribe(); };
  }, []);

  // 2) Chargement du contexte applicatif quand l'utilisateur change (effet séparé)
  const uid = session && session.user && session.user.id;
  useEffect(() => {
    let actif = true;
    (async () => {
      if (!session) { setProfile(null); setOrg(null); setRole(null); return; }

      const meta = (session.user && session.user.user_metadata) || {};
      const { data: prof } = await supabase.from("profiles").select("*").eq("id", uid).maybeSingle();
      if (!actif) return;
      setProfile(prof || { id: uid, email: session.user.email, full_name: meta.full_name || session.user.email });

      let { data: membership } = await supabase
        .from("org_members")
        .select("role, statut, organisation_id, organisations(id, nom, type, sieges)")
        .eq("user_id", uid).eq("statut", "actif").limit(1).maybeSingle();

      // On ne crée une organisation QUE si l'utilisateur n'a AUCUNE adhésion
      // (un membre suspendu ne doit pas se voir créer une organisation parasite).
      let doitBootstrap = false;
      if (!membership) {
        const { data: uneAdhesion } = await supabase
          .from("org_members").select("organisation_id").eq("user_id", uid).limit(1).maybeSingle();
        doitBootstrap = !uneAdhesion;
      }

      if (!membership && doitBootstrap) {
        // Nom de l'organisation = entreprise saisie à l'inscription (sinon repli)
        const orgNom = (meta.company && meta.company.trim())
          || `Organisation de ${meta.first_name || (session.user.email || "mon-espace").split("@")[0]}`;
        const { error } = await supabase.rpc("create_organisation", {
          p_nom: orgNom, p_type: "entreprise", p_taille: null, p_plan: "business",
        });
        if (!error) {
          const res = await supabase
            .from("org_members")
            .select("role, statut, organisation_id, organisations(id, nom, type, sieges)")
            .eq("user_id", uid).eq("statut", "actif").limit(1).maybeSingle();
          membership = res.data;
        }
      }
      if (!actif) return;
      if (membership) {
        setRole(membership.role);
        setOrg({
          id: membership.organisation_id,
          nom: membership.organisations ? membership.organisations.nom : "",
          sieges: membership.organisations ? membership.organisations.sieges : null,
          role: membership.role,
        });
      } else {
        setRole(null); setOrg(null);
      }
    })();
    return () => { actif = false; };
  }, [uid]); // eslint-disable-line

  // Mise à jour du profil (nom, prénom…) — disponible quel que soit le rôle.
  const updateProfile = useCallback(async (patch) => {
    if (!uid) return { error: new Error("Aucun utilisateur") };
    const { error } = await supabase.from("profiles").update(patch).eq("id", uid);
    if (!error) setProfile((p) => ({ ...(p || {}), ...patch }));
    return { error };
  }, [uid]);

  const signOut = useCallback(async () => { await supabase.auth.signOut(); }, []);
  const refresh = useCallback(async () => {
    if (!uid) return;
    const { data } = await supabase
      .from("org_members")
      .select("role, statut, organisation_id, organisations(id, nom, type, sieges)")
      .eq("user_id", uid).eq("statut", "actif").limit(1).maybeSingle();
    if (data) {
      setRole(data.role);
      setOrg({ id: data.organisation_id, nom: data.organisations && data.organisations.nom, sieges: data.organisations && data.organisations.sieges, role: data.role });
    }
  }, [uid]);

  const isSuperAdmin = role === "super_admin";
  const isAdmin = role === "admin" || role === "super_admin";
  const isManager = role === "admin" || role === "manager" || role === "super_admin";

  return (
    <SessionContext.Provider value={{ loading: !authReady, session, profile, org, role, isSuperAdmin, isAdmin, isManager, signOut, refresh, updateProfile }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    return { loading: true, session: null, profile: null, org: null, role: null, isSuperAdmin: false, isAdmin: false, isManager: false, signOut: () => {}, refresh: () => {}, updateProfile: async () => ({ error: null }) };
  }
  return ctx;
}
